// src/components/Admin/UserManager/UserList.jsx
// ────────────────────────────────────────────────────────────────
import React, {
    useState, useEffect, useMemo, useCallback,
} from 'react';
import {
    Box, TextField, Select, MenuItem, InputLabel, FormControl,
    TableContainer, Paper, Table, TableHead, TableRow,
    TableCell, TableBody, Pagination,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../../api/axios';
import UserItem from './UserItem';

const ROWS_PER_PAGE = 15;

export default function UserList({ userInfo }) {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [sortBy, setSortBy] = useState('username');
    const [page, setPage] = useState(1);

    /* ─────────── API ─────────── */
    const fetchUsers = useCallback(() => {
        api.get('/api/management/users/')
            .then((res) => setUsers(res.data))
            .catch(() => alert('유저 정보를 불러오는 데 실패했습니다.'));
    }, []);

    useEffect(fetchUsers, [fetchUsers]);

    /* ───────── 필터·정렬 ───────── */
    const filteredUsers = useMemo(
        () => users
            .filter((u) => (
                !search.trim()
                || ((u.nickname || '').toLowerCase().includes(search.toLowerCase()))
                || ((u.username || '').toLowerCase().includes(search.toLowerCase()))
            ))
            .filter((u) => roleFilter === 'all' || u.role === roleFilter)
            .sort((a, b) => {
                if (sortBy === 'points') return b.points - a.points;
                if (sortBy === 'date_joined') return new Date(b.date_joined) - new Date(a.date_joined);
                if (sortBy === 'last_login') return new Date(b.last_login) - new Date(a.last_login);
                return (a.nickname || a.username || '').localeCompare(b.nickname || b.username || '');
            }),
        [users, search, roleFilter, sortBy],
    );

    /* ───────── 페이지 ───────── */
    const paginated = filteredUsers.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE,
    );
    const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE) || 1;
    const handlePage = (_, v) => setPage(v);
    useEffect(() => setPage(1), [search, roleFilter, sortBy]);

    /* ───────── 렌더 ───────── */
    return (
        <Box>
            {/* 검색 / 필터 바 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    label="닉네임"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <FormControl size="small">
                    <InputLabel>권한</InputLabel>
                    <Select
                        value={roleFilter}
                        label="권한"
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="user">유저</MenuItem>
                        <MenuItem value="admin">어드민</MenuItem>
                        <MenuItem value="master">마스터</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>정렬 기준</InputLabel>
                    <Select
                        value={sortBy}
                        label="정렬 기준"
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <MenuItem value="username">닉네임순</MenuItem>
                        <MenuItem value="points">포인트순</MenuItem>
                        <MenuItem value="last_login">최근접속순</MenuItem>
                        <MenuItem value="date_joined">가입일순</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* 테이블 */}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    '& tr:hover': { bgcolor: 'grey.100' },
                    '& td, & th': {
                        borderBottom: '1px solid #eee',
                        textAlign: 'center',
                        p: 1.25,
                        whiteSpace: 'nowrap',
                        /* 모바일: 패딩·폰트 축소 */
                        '@media (max-width:600px)': {
                            p: 0.75,
                            fontSize: '0.8rem',
                        },
                    },
                    '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                    /* 모바일: 전체 테이블 축소 & 스크롤 */
                    '@media (max-width:600px)': {
                        transform: 'scale(0.9)',
                        transformOrigin: 'top left',
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell width={130}>닉네임</TableCell>
                            <TableCell width={120}>포인트</TableCell>
                            <TableCell width={160}>가입일</TableCell>
                            <TableCell width={160}>최근 접속일</TableCell>
                            <TableCell width={90}>상태</TableCell>
                            <TableCell width={120}>권한</TableCell>
                            <TableCell width={240}>관리</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ py: 5 }}>
                                    🔍 조건에 맞는 유저가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((u) => (
                                <UserItem
                                    key={u.id}
                                    user={u}
                                    userInfo={userInfo}
                                    refreshUsers={fetchUsers}
                                >
                                    {/* 가입일 */}
                                    <TableCell align="center">
                                        {dayjs(u.date_joined).format('YYYY-MM-DD')}
                                    </TableCell>

                                    {/* 최근접속 */}
                                    <TableCell align="center">
                                        {u.last_login
                                            ? dayjs(u.last_login).format('YYYY-MM-DD')
                                            : '-'}
                                    </TableCell>
                                </UserItem>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePage}
                        shape="rounded"
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
}
